// The little music theory the director needs (the music pass, 2026-10-19; audio card 8): pitch classes, a scale's
// note pool, the nearest chord tone, and voice leading: a chord voiced in a range as the voicing nearest the one
// before it (each voice moving as little as it can), so the harmony moves smoothly instead of jumping between
// root-position blocks. Hand-rolled (about a hundred lines) rather than a library: the director needs voicing by
// distance, which a chord-naming library doesn't do. Pure functions over MIDI numbers.

/** A note's pitch class relative to a key centre (0..11). */
export const pcOf = (midi: number, root = 0): number => ((((midi - root) % 12) + 12) % 12);

/** Every MIDI note in [low, high] whose pitch class (relative to `root`) is in `scale`, low to high. */
export function scalePool(root: number, scale: readonly number[], low: number, high: number): number[] {
  const set = new Set(scale.map((s) => pcOf(s)));
  const out: number[] = [];
  for (let n = low; n <= high; n++) if (set.has(pcOf(n, root))) out.push(n);
  return out;
}

/** The index in `pool` of the note nearest `midi` (ties go low). */
export function nearestIndex(pool: readonly number[], midi: number): number {
  let best = 0;
  for (let i = 1; i < pool.length; i++) if (Math.abs(pool[i]! - midi) < Math.abs(pool[best]! - midi)) best = i;
  return best;
}

/** The index in `pool` of the chord tone nearest index `i` (searching outwards; `i` itself when none is a chord tone). */
export function snapToChord(pool: readonly number[], i: number, root: number, chord: readonly number[]): number {
  const tones = new Set(chord.map((c) => pcOf(c)));
  for (let d = 0; d < pool.length; d++) {
    if (i - d >= 0 && tones.has(pcOf(pool[i - d]!, root))) return i - d;
    if (i + d < pool.length && tones.has(pcOf(pool[i + d]!, root))) return i + d;
  }
  return i;
}

/** How to voice a chord. */
export interface VoicingOptions {
  /** The key centre (MIDI): chord tones are semitones above it. */
  root: number;
  /** The range every voice stays in (MIDI). */
  low: number;
  high: number;
  /** Voices (default: one per pitch class). */
  voices?: number;
  /** Leave the chord's root out when there are more pitch classes than voices (the bass has it). Default true. */
  omitRoot?: boolean;
}

/** The pitch classes to voice, most important first: the third (or sus) and colour tones, then the fifth, the root. */
function choosePcs(chord: readonly number[], n: number, omitRoot: boolean): number[] {
  const uniq: number[] = [];
  for (const c of chord) if (!uniq.includes(pcOf(c))) uniq.push(pcOf(c));
  if (uniq.length <= n) {
    // double from the root up (root, then fifth) until every voice has a note
    const out = [...uniq];
    for (let k = 0; out.length < n; k++) out.push(uniq[k % Math.min(2, uniq.length)]!);
    return out;
  }
  // [root, third, fifth, ...colours]: drop the fifth first, then the root (when allowed), then the last colours
  const [r, third, fifth, ...colours] = uniq;
  const order = [third, ...colours, fifth, r].filter((x): x is number => x !== undefined);
  const keep = order.slice(0, n);
  if (!omitRoot && r !== undefined && !keep.includes(r)) keep[keep.length - 1] = r;
  return keep;
}

/** Close position from the chord's root upwards, at the lowest octave in range (a block chord; no voice leading). */
export function blockVoicing(chord: readonly number[], o: VoicingOptions): number[] {
  const pcs = choosePcs(chord, o.voices ?? chord.length, o.omitRoot ?? true);
  const base = o.root + pcOf(chord[0] ?? 0);
  let start = base;
  while (start - 12 >= o.low) start -= 12;
  while (start < o.low) start += 12;
  const out: number[] = [];
  let last = start - 1;
  for (const pc of [...pcs].sort((a, b) => pcOf(a - (chord[0] ?? 0)) - pcOf(b - (chord[0] ?? 0)))) {
    let n = o.root + pc;
    while (n <= last) n += 12;
    while (n - 12 > last) n -= 12;
    out.push(n);
    last = n;
  }
  return out.sort((a, b) => a - b);
}

/** Voice a chord in range as the voicing nearest `prev`: every set of `voices` distinct chord-tone notes in range
 *  that holds the third (or the sus tone) and as many pitch classes as it can, scored by the voices' total movement
 *  (matched low to high), a pull towards the range's middle so the register doesn't drift, a penalty for a semitone
 *  cluster between neighbours or a second low down (under G3), for a colour tone (the seventh, the ninth) left out, and for the root kept when
 *  `omitRoot` and something else could take its place. So a dominant seventh can drop its fifth and keep its root,
 *  third and seventh when that moves least (a shell voicing). With no `prev`, the voicing nearest the middle. */
export function voiceLead(chord: readonly number[], prev: readonly number[] | null, o: VoicingOptions): number[] {
  const uniq = [...new Set(chord.map((c) => pcOf(c)))];
  const n = o.voices ?? uniq.length;
  const omitRoot = o.omitRoot ?? true;
  const centre = (o.low + o.high) / 2;
  const notes: number[] = [];
  for (let m = o.low; m <= o.high; m++) if (uniq.includes(pcOf(m, o.root))) notes.push(m);
  const third = uniq[1];
  const colours = uniq.slice(3);
  const want = Math.min(n, uniq.length);
  let best: number[] | null = null;
  let bestCost = Infinity;
  const pick: number[] = [];
  const score = (v: number[]): void => {
    if (v[v.length - 1]! - v[0]! > 19) return;
    const pcs = new Set(v.map((x) => pcOf(x, o.root)));
    if (pcs.size < want) return;
    if (third !== undefined && !pcs.has(third)) return;
    let cost = 0;
    if (prev && prev.length === v.length) for (let i = 0; i < v.length; i++) cost += Math.abs(v[i]! - prev[i]!);
    else if (prev && prev.length) for (const x of v) cost += Math.min(...prev.map((p) => Math.abs(p - x)));
    const mean = v.reduce((a, b) => a + b, 0) / v.length;
    cost += Math.abs(mean - centre) * (prev ? 0.25 : 1);
    // a semitone cluster anywhere, or a second low down (mud: the low interval limit), costs extra
    for (let i = 1; i < v.length; i++) if (v[i]! - v[i - 1]! === 1 || (v[i]! - v[i - 1]! <= 2 && v[i - 1]! < 55)) cost += 2.5;
    for (const c of colours) if (!pcs.has(c)) cost += 1.5;
    if (uniq.length > n && pcs.has(uniq[0]!)) cost += omitRoot ? 1 : -1;
    if (!omitRoot && !pcs.has(uniq[0]!)) cost += 2;
    if (cost < bestCost) {
      bestCost = cost;
      best = [...v];
    }
  };
  const walk = (from: number): void => {
    if (pick.length === n) return score(pick);
    for (let i = from; i <= notes.length - (n - pick.length); i++) {
      pick.push(notes[i]!);
      walk(i + 1);
      pick.pop();
    }
  };
  walk(0);
  return best ?? blockVoicing(chord, o);
}

/** The largest movement of any voice between two voicings of the same size (semitones; voices matched low to high). */
export function maxMovement(a: readonly number[], b: readonly number[]): number {
  let m = 0;
  for (let i = 0; i < Math.min(a.length, b.length); i++) m = Math.max(m, Math.abs(a[i]! - b[i]!));
  return m;
}
